import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ClipboardList, FileText, BarChart3, ShieldCheck, ClipboardCheck, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useUiStore } from '@/store/ui.store';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import { useDebounce } from '@/lib/hooks/useDebounce';

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  type: 'vendor' | 'assessment';
  href: string;
}

// Per-type result badge (net-new — nav-shell Slice B2). Uses existing badge variants.
const TYPE_BADGE: Record<SearchResult['type'], { label: string; variant: 'sky' | 'amber' }> = {
  vendor: { label: 'Vendor', variant: 'sky' },
  assessment: { label: 'Assessment', variant: 'amber' },
};

// Quick-links card grid (nav-shell F3). Each maps to a REAL route + the same module/role gating
// the sidebar uses — a tile the user can't access is not rendered (no dead links / 403s).
interface QuickLink { label: string; href: string; icon: LucideIcon; roles?: string[]; module?: string }
const QUICK_LINKS: QuickLink[] = [
  { label: 'Vendors',             href: '/vendors',                    icon: Building2,     roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer'], module: 'vrm' },
  { label: 'Assessments',         href: '/assessments',                icon: ClipboardList, roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer'], module: 'vrm' },
  { label: 'Framework Programs',  href: '/compliance/workspace/frameworks', icon: ShieldCheck, roles: ['super_admin', 'risk_manager', 'analyst', 'compliance_manager'], module: 'compliance' },
  { label: 'Evidence Repository', href: '/compliance/workspace/tasks?tab=evidence', icon: FileText,      roles: ['super_admin', 'risk_manager', 'compliance_manager'],           module: 'compliance' },
  { label: 'Tasks',               href: '/compliance/workspace/tasks', icon: ClipboardCheck,                                                                       module: 'compliance' },
  // ⚠ RET-10: Compliance-gated, like its Sidebar entry (it was offered to tenants without Compliance).
  { label: 'Compliance',          href: '/dashboards/compliance',      icon: BarChart3,     roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer', 'compliance_manager'], module: 'compliance' },
];

export function GlobalSearch() {
  const { searchOpen, setSearchOpen } = useUiStore();
  const { isAuthenticated, roles, entitledModules } = useAuthStore();

  const canSeeQuickLink = (ql: QuickLink) =>
    (!ql.module || entitledModules.includes(ql.module)) &&
    (!ql.roles || ql.roles.some((r) => roles.includes(r)));
  const quickLinks = QUICK_LINKS.filter(canSeeQuickLink);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(!searchOpen);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchOpen, setSearchOpen]);

  const search = useCallback(async (q: string) => {
    if (!q.trim() || !isAuthenticated) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const [vendors] = await Promise.allSettled([
        apiClient.get(`/vendors?search=${encodeURIComponent(q)}&limit=5`),
      ]);

      const out: SearchResult[] = [];

      if (vendors.status === 'fulfilled') {
        const items: any[] = Array.isArray(vendors.value.data?.data)
          ? vendors.value.data.data
          : [];
        for (const v of items.slice(0, 5)) {
          out.push({
            id: v.id,
            label: v.name,
            sublabel: v.tier ? `Tier: ${v.tier}` : undefined,
            type: 'vendor',
            href: `/vendors/${v.id}`,
          });
        }
      }

      setResults(out);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    search(debouncedQuery);
  }, [debouncedQuery, search]);

  function handleSelect(href: string) {
    setSearchOpen(false);
    setQuery('');
    navigate(href);
  }

  function handleOpenChange(open: boolean) {
    setSearchOpen(open);
    if (!open) setQuery('');
  }

  const vendorResults = results.filter((r) => r.type === 'vendor');

  return (
    <CommandDialog open={searchOpen} onOpenChange={handleOpenChange}>
      <CommandInput
        placeholder="Search vendors, assessments…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {!query && quickLinks.length > 0 && (
          <div className="p-3">
            <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">Quick Links</p>
            <div className="grid grid-cols-2 gap-2">
              {quickLinks.map((ql) => (
                <CommandItem
                  key={ql.href}
                  value={`quick-${ql.label}`}
                  onSelect={() => handleSelect(ql.href)}
                  className="flex items-center gap-2.5 rounded-lg border border-border-subtle bg-bg-surface-2 px-3 py-2.5 data-[selected=true]:border-accent-cyan data-[selected=true]:bg-bg-surface"
                >
                  <ql.icon className="h-4 w-4 shrink-0 text-text-secondary" />
                  <span className="truncate text-sm text-text-primary">{ql.label}</span>
                </CommandItem>
              ))}
            </div>
          </div>
        )}

        {query && !loading && results.length === 0 && (
          <CommandEmpty>No results for "{query}"</CommandEmpty>
        )}

        {loading && (
          <div className="py-6 text-center text-sm text-text-secondary">Searching…</div>
        )}

        {vendorResults.length > 0 && (
          <>
            <CommandGroup heading="Vendors">
              {vendorResults.map((r) => (
                <CommandItem key={r.id} value={`vendor-${r.id}`} onSelect={() => handleSelect(r.href)}>
                  <Building2 className="text-text-secondary" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{r.label}</span>
                    {r.sublabel && (
                      <span className="truncate text-xs text-text-secondary">{r.sublabel}</span>
                    )}
                  </div>
                  <Badge variant={TYPE_BADGE[r.type].variant} className="ml-auto shrink-0 text-[10px]">
                    {TYPE_BADGE[r.type].label}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

      </CommandList>
    </CommandDialog>
  );
}
